import React, { useState, useEffect } from 'react';
import { Box, Grid, Flex, Text, Spinner } from '@chakra-ui/react';

const StatItem = ({ value, label }) => (
  <Flex direction="column" align="center" justify="center">
    <Text mb={2} fontSize="3xl" fontWeight="extrabold">
      {value || '--'}
    </Text>
    <Text color="gray.500">{label}</Text>
  </Flex>
);

const Statistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalSupply, setTotalSupply] = useState(null);

  const fetchStatistics = async () => {
    try {
      const [statsResponse, supplyResponse] = await Promise.all([
        fetch('https://api-explorer.nervosscan.com/api/v1/statistics', {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
          },
        }),
        fetch('https://api-explorer.nervosscan.com/api/v1/daily_statistics/circulating_supply-burnt-locked_capacity', {
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
          },
        })
      ]);

      const [statsData, supplyData] = await Promise.all([
        statsResponse.json(),
        supplyResponse.json()
      ]);

      // Get the latest supply data
      const latestSupply = supplyData.data[supplyData.data.length - 1].attributes;
      setTotalSupply(latestSupply.circulating_supply);
      setStats(statsData.data.attributes);
      setError(null);
    } catch (err) {
      setError('Failed to load statistics');
      console.error('Error fetching statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
    const interval = setInterval(fetchStatistics, 60000);
    return () => clearInterval(interval);
  }, []);

  const formatTxLast24h = (value) => {
    return value ? `${(value / 1000).toFixed(2)}K` : '--';
  };

  const formatHashRate = (value) => {
    if (!value) return '--';
    const hash = value / 1000000000000;
    return `${hash.toFixed(2)} PH/s`;
  };

  const formatMiningDifficulty = (value) => {
    if (!value) return '--';
    return `${(value / Math.pow(10, 18)).toFixed(2)} EH`;
  };

  const formatAverageBlockTime = (value) => {
    if (!value) return '--';
    return `${(stats.average_block_time / 1000).toFixed(2)} s`;
  };

  const formatEpochTime = (value) => {
    if (!value) return '--';
    const hours = Math.floor(value / 1000 / 3600)
    const minutes = Math.floor((value / 1000 % 3600) / 60)
    return `${hours}h${minutes}m`
  };

  const formatCKB = (value) => {
    if (!value) return '--';
    // 先转换为CKB单位（除以100000000），再转换为十亿为单位（除以1000000000）
    const billion = Number(value) / 100000000 / 1000000000;
    return `${billion.toFixed(2)}B`;
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Text color="red.500">{error}</Text>
      </Flex>
    );
  }

  return (
    <Box bg="white" rounded="lg" p={4} md={{ p: 8 }} shadow="md">
      <Grid
        templateColumns={{ base: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' }}
        gap={8}
        p={4}
        sm={{ p: 8 }}
      >
        <StatItem
          value={formatCKB(totalSupply)}
          label="Total Supply"
        />
        <StatItem
          value={stats?.transactions_count_per_minute}
          label="Transactions per Minute"
        />
        <StatItem
          value={formatTxLast24h(stats?.transactions_last_24hrs)}
          label="Transactions (24h)"
        />
        <StatItem
          value={formatAverageBlockTime(stats?.average_block_time)}
          label="Average Block Time"
        />
        <StatItem
          value={formatHashRate(stats?.hash_rate)}
          label="Mining Hash Rate"
        />
        <StatItem
          value={formatMiningDifficulty(stats?.current_epoch_difficulty)}
          label="Mining Difficulty"
        />
        <StatItem
          value={stats?.epoch_info.epoch_number}
          label="Epoch"
        />
        <StatItem
          value={formatEpochTime(stats?.estimated_epoch_time)}
          label="Estimated Epoch Time"
        />
      </Grid>
    </Box>
  );
};

export default Statistics;
